import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, Clock, CalendarDays, ArrowLeft, Loader2, Phone } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format, addDays, isBefore, startOfDay } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_40bbf581-5235-4696-9bdc-3ce549288854/artifacts/onxdn5ml_PBlogo.jpg";
const PHONE_NUMBER = "+15054727064";
const PHONE_DISPLAY = "(505) 472-7064";
const SCHEDULING_WEBHOOK_URL = "https://services.leadconnectorhq.com/hooks/ejeOEWkR6MEM49HF2Eqp/webhook-trigger/48944a2c-94eb-44aa-8e6a-9ff7d55f7921";

const TIME_SLOTS = [
  { time: "10:00 AM", value: "10:00" },
  { time: "2:00 PM", value: "14:00" },
  { time: "4:00 PM", value: "16:00" },
];

export default function Scheduling() {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBooked, setIsBooked] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);

  // Get lead data from navigation state
  const leadData = location.state || {};
  const { leadId, name, email, phone } = leadData;

  useEffect(() => {
    // If no lead data, redirect to home
    if (!leadId) {
      // Allow viewing the page anyway for demo purposes
      console.log("No lead data found, but allowing page view for demo");
    }
  }, [leadId]);

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime(null); // Reset time when date changes
  };

  const handleTimeSelect = (timeSlot) => {
    setSelectedTime(timeSlot);
  };

  const handleSubmit = async () => {
    if (!selectedDate || !selectedTime) {
      toast.error("Please select both a date and time");
      return;
    }

    setIsSubmitting(true);

    try {
      const appointmentData = {
        lead_id: leadId || "demo-lead",
        name: name || "Demo User",
        email: email || "demo@example.com",
        phone: phone || "(505) 555-0000",
        appointment_date: format(selectedDate, "yyyy-MM-dd"),
        appointment_time: selectedTime.value,
      };

      const response = await axios.post(`${API}/appointments`, appointmentData);

      // Send to scheduling webhook (GoHighLevel)
      try {
        await axios.post(SCHEDULING_WEBHOOK_URL, {
          ...appointmentData,
          appointment_time_display: selectedTime.time,
          submitted_at: new Date().toISOString(),
        });
      } catch (webhookError) {
        console.error("Scheduling webhook error:", webhookError);
      }

      // Push to dataLayer
      if (typeof window !== "undefined" && window.dataLayer) {
        window.dataLayer.push({
          event: "appointment_booked",
          appointment_id: response.data.id,
          appointment_date: appointmentData.appointment_date,
          appointment_time: selectedTime.time,
        });
      }

      setBookingDetails({
        date: format(selectedDate, "EEEE, MMMM d, yyyy"),
        time: selectedTime.time,
      });
      setIsBooked(true);
      toast.success("Request submitted! We'll call you shortly to confirm.");
    } catch (error) {
      console.error("Booking error:", error);
      toast.error("Unable to schedule. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Disable past dates
  const disabledDays = (date) => {
    return isBefore(date, startOfDay(new Date()));
  };

  if (isBooked) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-4" data-testid="booking-confirmation">
        <Card className="w-full max-w-lg shadow-2xl border-0">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4 font-['Playfair_Display']" data-testid="confirmation-title">
              Almost There!
            </h1>
            
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <p className="text-amber-800 font-medium text-sm leading-relaxed">
                A member of our team will give you a call to confirm your appointment. Due to high demand and limited availability, you will need to confirm by phone within an hour.
              </p>
            </div>

            <div className="bg-[#1E4C9A]/5 p-6 rounded-xl mb-6">
              <p className="text-sm text-gray-600 mb-3">Your Requested Time:</p>
              <div className="flex items-center justify-center gap-3 mb-2">
                <CalendarDays className="h-5 w-5 text-[#1E4C9A]" />
                <span className="font-semibold text-gray-900">{bookingDetails?.date}</span>
              </div>
              <div className="flex items-center justify-center gap-3">
                <Clock className="h-5 w-5 text-[#1E4C9A]" />
                <span className="font-semibold text-gray-900">{bookingDetails?.time}</span>
              </div>
            </div>

            <p className="text-sm text-gray-500 mb-6">
              We'll reach out to {phone || "your phone"} shortly.
            </p>

            <Button
              onClick={() => navigate("/")}
              className="bg-[#1E4C9A] hover:bg-[#153975] text-white font-semibold px-8 py-3 rounded-full"
              data-testid="back-to-home"
            >
              Back to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="scheduling-page">
      {/* Header */}
      <header className="bg-[#1E4C9A] py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <a href="/">
            <img src={LOGO_URL} alt="Prime Baths of NM" className="h-10 w-auto brightness-0 invert" />
          </a>
          <a 
            href={`tel:${PHONE_NUMBER}`}
            className="flex items-center gap-2 text-white hover:text-white/80 transition-colors"
          >
            <Phone className="h-4 w-4" />
            <span className="font-semibold">{PHONE_DISPLAY}</span>
          </a>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-[#1E4C9A] mb-8 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back</span>
        </button>

        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 font-['Playfair_Display']" data-testid="scheduling-title">
            You're One Step Closer to Your Dream Bath
          </h1>
          <p className="text-gray-600 text-lg">
            Choose a time for your free, no-obligation design consultation.
          </p>
        </div>

        {/* Scheduling Card */}
        <Card className="shadow-xl border-0 overflow-hidden">
          <CardHeader className="bg-[#1E4C9A] text-white p-6">
            <CardTitle className="flex items-center gap-3 font-['Playfair_Display']">
              <CalendarDays className="h-6 w-6" />
              Schedule Your Free Consultation
            </CardTitle>
          </CardHeader>
          <CardContent className="p-8">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Calendar */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 bg-[#1E4C9A] text-white rounded-full flex items-center justify-center text-sm">1</span>
                  Select a Date
                </h3>
                <div className="border rounded-xl p-4 bg-white" data-testid="calendar-container">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={handleDateSelect}
                    disabled={disabledDays}
                    fromDate={new Date()}
                    toDate={addDays(new Date(), 60)}
                    className="rounded-md"
                  />
                </div>
              </div>

              {/* Time Slots */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 bg-[#1E4C9A] text-white rounded-full flex items-center justify-center text-sm">2</span>
                  Select a Time
                </h3>
                <div className="space-y-3" data-testid="time-slots-container">
                  {TIME_SLOTS.map((slot) => (
                    <button
                      key={slot.value}
                      onClick={() => handleTimeSelect(slot)}
                      disabled={!selectedDate}
                      className={`time-slot w-full p-4 rounded-xl border-2 text-left flex items-center gap-3 ${
                        selectedTime?.value === slot.value
                          ? "selected border-[#1E4C9A] bg-[#1E4C9A] text-white"
                          : "border-gray-200 hover:border-[#1E4C9A] bg-white"
                      } ${!selectedDate ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                      data-testid={`time-slot-${slot.value}`}
                    >
                      <Clock className={`h-5 w-5 ${selectedTime?.value === slot.value ? "text-white" : "text-[#1E4C9A]"}`} />
                      <span className="font-semibold">{slot.time}</span>
                    </button>
                  ))}
                </div>

                {selectedDate && !selectedTime && (
                  <p className="text-sm text-gray-500 mt-4">
                    Please select a time slot for {format(selectedDate, "MMMM d, yyyy")}
                  </p>
                )}

                {selectedDate && selectedTime && (
                  <div className="mt-6 p-4 bg-[#1E4C9A]/5 rounded-xl">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold">Selected:</span>{" "}
                      {format(selectedDate, "EEEE, MMMM d, yyyy")} at {selectedTime.time}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="mt-8 pt-8 border-t">
              <Button
                onClick={handleSubmit}
                disabled={!selectedDate || !selectedTime || isSubmitting}
                className="w-full h-14 bg-[#1E4C9A] hover:bg-[#153975] text-white font-semibold text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50"
                data-testid="confirm-booking-button"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Scheduling...
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-5 w-5" />
                    Confirm My Consultation
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Info Section */}
        <div className="mt-8 grid sm:grid-cols-3 gap-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-[#1E4C9A]/10 rounded-full flex items-center justify-center flex-shrink-0">
              <CheckCircle className="h-5 w-5 text-[#1E4C9A]" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Free Consultation</h4>
              <p className="text-sm text-gray-600">No cost, no obligation</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-[#1E4C9A]/10 rounded-full flex items-center justify-center flex-shrink-0">
              <Clock className="h-5 w-5 text-[#1E4C9A]" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">30-45 Minutes</h4>
              <p className="text-sm text-gray-600">Quick and convenient</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-[#1E4C9A]/10 rounded-full flex items-center justify-center flex-shrink-0">
              <CalendarDays className="h-5 w-5 text-[#1E4C9A]" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Flexible Scheduling</h4>
              <p className="text-sm text-gray-600">Choose a time that works for you</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
